<template>
  <div class="admin-panel">
    <div class="admin-header">
      <div class="header-content">
        <h1>Административная панель</h1>
        <p>Мониторинг и управление приложением</p>
      </div>
      <div class="header-actions">
        <a-button @click="logout" type="text" class="logout-button">
          Выйти
        </a-button>
      </div>
    </div>

    <!-- Навигация по разделам -->
    <a-tabs v-model:activeKey="activeTab" class="admin-tabs">
      <!-- Отображение ошибок -->
      <div v-if="error" class="error-banner">
        <a-alert
          :message="error"
          type="error"
          show-icon
          closable
          @close="clearError"
        />
      </div>
      
      <a-tab-pane key="overview" tab="Обзор">
        <div class="stats-overview">
          <!-- Карточки статистики -->
          <div class="stats-grid">
            <a-card class="stat-card">
              <div class="stat-content">
                <div class="stat-icon users-icon">👥</div>
                <div class="stat-info">
                  <div class="stat-value">{{ stats.users?.total || 0 }}</div>
                  <div class="stat-label">Пользователей</div>
                  <div class="stat-detail">
                    +{{ stats.users?.newToday || 0 }} сегодня
                  </div>
                </div>
              </div>
            </a-card>

            <a-card class="stat-card">
              <div class="stat-content">
                <div class="stat-icon teams-icon">🏒</div>
                <div class="stat-info">
                  <div class="stat-value">{{ stats.teams?.total || 0 }}</div>
                  <div class="stat-label">Команд</div>
                  <div class="stat-detail">
                    {{ stats.teams?.avgSize || 0 }} игроков в среднем
                  </div>
                </div>
              </div>
            </a-card>

            <a-card class="stat-card">
              <div class="stat-content">
                <div class="stat-icon events-icon">📅</div>
                <div class="stat-info">
                  <div class="stat-value">{{ stats.events?.total || 0 }}</div>
                  <div class="stat-label">Событий</div>
                  <div class="stat-detail">
                    {{ stats.events?.thisWeek || 0 }} на этой неделе
                  </div>
                </div>
              </div>
            </a-card>

            <a-card class="stat-card">
              <div class="stat-content">
                <div class="stat-icon invitations-icon">📨</div>
                <div class="stat-info">
                  <div class="stat-value">{{ stats.invitations?.conversionRate || 0 }}%</div>
                  <div class="stat-label">Конверсия приглашений</div>
                  <div class="stat-detail">
                    {{ stats.invitations?.used || 0 }} из {{ stats.invitations?.total || 0 }}
                  </div>
                </div>
              </div>
            </a-card>
          </div>

          <!-- График активности -->
          <a-card class="activity-chart-card">
            <template #title>
              <span>Активность за последние 7 дней</span>
            </template>
            <div class="activity-chart">
              <div 
                v-for="(day, index) in activityChartData" 
                :key="index"
                class="chart-bar"
                :style="{ height: `${day.height}%` }"
                :title="`${day.date}: ${day.count} событий`"
              >
                <div class="bar-value">{{ day.count }}</div>
              </div>
            </div>
            <div class="chart-labels">
              <span v-for="(day, index) in activityChartData" :key="index">
                {{ formatChartDate(day.date) }}
              </span>
            </div>
          </a-card>

          <!-- Популярные города -->
          <a-card class="cities-card">
            <template #title>
              <span>Популярные города</span>
            </template>
            <div class="cities-list">
              <div 
                v-for="city in stats.teams?.popularCities || []" 
                :key="city.city"
                class="city-item"
              >
                <span class="city-name">{{ city.city || 'Не указан' }}</span>
                <span class="city-count">{{ city.count }} команд</span>
              </div>
            </div>
          </a-card>
        </div>
      </a-tab-pane>

      <a-tab-pane key="users" tab="Пользователи">
        <div class="users-section">
          <!-- Поиск -->
          <div class="search-section">
            <a-input-search
              v-model:value="userSearch"
              placeholder="Поиск пользователей..."
              @search="loadUsers(1, userPagination.pageSize)"
              @input="debouncedSearch('users')"
              style="max-width: 300px"
            />
          </div>

          <!-- Таблица пользователей -->
          <a-table
            :columns="userColumns"
            :data-source="users"
            :loading="usersLoading"
            :pagination="userPagination"
            @change="handleUserTableChange"
            row-key="id"
            :locale="{ emptyText: 'Нет пользователей для отображения' }"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'name'">
                <div class="user-info">
                  <a-avatar :src="record.photo_url" :size="32" />
                  <span>{{ record.last_name }} {{ record.first_name }}</span>
                </div>
              </template>
              <template v-else-if="column.key === 'username'">
                <a 
                  v-if="record.username"
                  :href="`https://t.me/${record.username}`" 
                  target="_blank"
                  class="username-link"
                >
                  @{{ record.username }}
                </a>
                <span v-else class="no-username">—</span>
              </template>
              <template v-else-if="column.key === 'created_at'">
                {{ formatDate(record.created_at) }}
              </template>
            </template>
          </a-table>
        </div>
      </a-tab-pane>

      <a-tab-pane key="teams" tab="Команды">
        <div class="teams-section">
          <!-- Поиск -->
          <div class="search-section">
            <a-input-search
              v-model:value="teamSearch"
              placeholder="Поиск команд..."
              @search="loadTeams(1, teamPagination.pageSize)"
              @input="debouncedSearch('teams')"
              style="max-width: 300px"
            />
          </div>

          <!-- Таблица команд -->
          <a-table
            :columns="teamColumns"
            :data-source="teams"
            :loading="teamsLoading"
            :pagination="teamPagination"
            @change="handleTeamTableChange"
            row-key="id"
            :locale="{ emptyText: 'Нет команд для отображения' }"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'name'">
                <div class="team-info">
                  <span class="team-name">{{ record.name }}</span>
                  <span class="team-city">{{ record.city }}</span>
                </div>
              </template>
              <template v-else-if="column.key === 'created_at'">
                {{ formatDate(record.created_at) }}
              </template>
            </template>
          </a-table>
        </div>
      </a-tab-pane>

      <a-tab-pane key="players" tab="Игроки">
        <div class="players-section">
          <!-- Фильтры -->
          <div class="filters-section">
            <div class="filters-row">
              <a-input-search
                v-model:value="playerSearch"
                placeholder="Поиск игроков..."
                @search="loadPlayers(1, playerPagination.pageSize)"
                @input="debouncedSearch('players')"
                style="max-width: 300px"
              />
              <a-select
                v-model:value="playerTeamFilter"
                placeholder="Фильтр по команде"
                style="width: 200px"
                allow-clear
                @change="debouncedSearch('players')"
              >
                <a-select-option value="">Все команды</a-select-option>
                <a-select-option v-for="team in teams" :key="team.id" :value="team.name">
                  {{ team.name }}
                </a-select-option>
              </a-select>
              <a-select
                v-model:value="playerPositionFilter"
                placeholder="Фильтр по позиции"
                style="width: 150px"
                allow-clear
                @change="debouncedSearch('players')"
              >
                <a-select-option value="">Все позиции</a-select-option>
                <a-select-option value="forward">Нападающий</a-select-option>
                <a-select-option value="defender">Защитник</a-select-option>
                <a-select-option value="goalkeeper">Вратарь</a-select-option>
                <a-select-option value="coach">Тренер</a-select-option>
                <a-select-option value="service">Обслуживающий персонал</a-select-option>
              </a-select>
            </div>
          </div>

          <!-- Таблица игроков -->
          <a-table
            :columns="playerColumns"
            :data-source="players"
            :loading="playersLoading"
            :pagination="playerPagination"
            @change="handlePlayerTableChange"
            row-key="player_id"
            :scroll="{ x: 1500 }"
            :locale="{ emptyText: 'Нет игроков для отображения' }"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'player_info'">
                <div class="player-info">
                  <a-avatar :src="record.photo_url" :size="40" />
                  <div class="player-details">
                    <div class="player-name">{{ record.last_name }} {{ record.first_name }}</div>
                    <div class="player-number">№{{ record.number || '—' }}</div>
                  </div>
                </div>
              </template>
              <template v-else-if="column.key === 'team_info'">
                <div class="team-info">
                  <div class="team-name">{{ record.team_name }}</div>
                  <div class="team-city">{{ record.team_city }}</div>
                </div>
              </template>
              <template v-else-if="column.key === 'position'">
                <a-tag :color="getPositionColor(record.position)">
                  {{ getPositionLabel(record.position) }}
                </a-tag>
              </template>
              <template v-else-if="column.key === 'role'">
                <a-tag :color="record.role === 'admin' ? 'red' : 'blue'">
                  {{ record.role === 'admin' ? 'Администратор' : 'Игрок' }}
                </a-tag>
              </template>
              <template v-else-if="column.key === 'captain_status'">
                <a-tag v-if="record.captain_status" :color="getCaptainColor(record.captain_status)">
                  {{ getCaptainLabel(record.captain_status) }}
                </a-tag>
                <span v-else class="no-status">—</span>
              </template>
              <template v-else-if="column.key === 'physical_info'">
                <div class="physical-info">
                  <div v-if="record.age">Возраст: {{ record.age }}</div>
                  <div v-if="record.height">Рост: {{ record.height }} см</div>
                  <div v-if="record.weight">Вес: {{ record.weight }} кг</div>
                </div>
              </template>
              <template v-else-if="column.key === 'skills'">
                <div class="skills-info">
                  <div v-if="record.stick_hand">Рука: {{ getStickHandLabel(record.stick_hand) }}</div>
                  <div v-if="record.skill_level">Уровень: {{ getSkillLevelLabel(record.skill_level) }}</div>
                </div>
              </template>
              <template v-else-if="column.key === 'participation_stats'">
                <div class="participation-stats">
                  <div>Всего: {{ record.events_participated }}</div>
                  <div>Подтверждено: {{ record.events_confirmed }}</div>
                  <div>Отклонено: {{ record.events_declined }}</div>
                </div>
              </template>
              <template v-else-if="column.key === 'status'">
                <a-tag :color="getStatusColor(record.player_status)">
                  {{ getStatusLabel(record.player_status) }}
                </a-tag>
              </template>
              <template v-else-if="column.key === 'created_at'">
                {{ formatDate(record.player_created_at) }}
              </template>
            </template>
          </a-table>
        </div>
      </a-tab-pane>

      <a-tab-pane key="system" tab="Система">
        <div class="system-section">
          <div class="system-grid">
            <!-- Информация о системе -->
            <a-card class="system-card">
              <template #title>
                <span>Системная информация</span>
              </template>
              <div class="system-info">
                <div class="info-item">
                  <span class="info-label">Платформа:</span>
                  <span class="info-value">{{ systemInfo.system?.platform }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Node.js:</span>
                  <span class="info-value">{{ systemInfo.system?.nodeVersion }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">CPU:</span>
                  <span class="info-value">{{ systemInfo.system?.cpu }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Аптайм:</span>
                  <span class="info-value">{{ formatUptime(systemInfo.system?.uptime) }}</span>
                </div>
              </div>
            </a-card>

            <!-- Использование памяти -->
            <a-card class="system-card">
              <template #title>
                <span>Использование памяти</span>
              </template>
              <div class="memory-info">
                <div class="memory-bar">
                  <div 
                    class="memory-used"
                    :style="{ width: `${memoryUsagePercent}%` }"
                  ></div>
                </div>
                <div class="memory-details">
                  <div class="memory-item">
                    <span>Использовано:</span>
                    <span>{{ formatBytes(systemInfo.system?.memory?.used) }}</span>
                  </div>
                  <div class="memory-item">
                    <span>Свободно:</span>
                    <span>{{ formatBytes(systemInfo.system?.memory?.free) }}</span>
                  </div>
                  <div class="memory-item">
                    <span>Всего:</span>
                    <span>{{ formatBytes(systemInfo.system?.memory?.total) }}</span>
                  </div>
                </div>
              </div>
            </a-card>

            <!-- База данных -->
            <a-card class="system-card">
              <template #title>
                <span>База данных</span>
              </template>
              <div class="database-info">
                <div class="info-item">
                  <span class="info-label">Размер:</span>
                  <span class="info-value">{{ systemInfo.database?.sizeFormatted }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">WebSocket подключения:</span>
                  <span class="info-value">{{ systemInfo.websocket?.activeConnections }}</span>
                </div>
              </div>
            </a-card>
          </div>
        </div>
      </a-tab-pane>
    </a-tabs>
  </div>
</template>

<script>
import { api } from '../services/api';

export default {
  name: 'AdminPanel',
  data() {
    return {
      activeTab: 'overview',
      stats: {},
      users: [],
      teams: [],
      players: [],
      systemInfo: {},
      usersLoading: false,
      teamsLoading: false,
      playersLoading: false,
      userSearch: '',
      teamSearch: '',
      playerSearch: '',
      playerTeamFilter: '',
      playerPositionFilter: '',
      // Добавляем состояние для обработки ошибок
      error: null,
      // Добавляем debounce для поиска
      searchTimeout: null,
      userPagination: {
        current: 1,
        pageSize: 20,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
      },
      teamPagination: {
        current: 1,
        pageSize: 20,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
      },
      playerPagination: {
        current: 1,
        pageSize: 20,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
      },
      userColumns: [
        {
          title: 'Пользователь',
          key: 'name',
          width: 200,
        },
        {
          title: 'Username',
          key: 'username',
          width: 150,
        },
        {
          title: 'Команд',
          dataIndex: 'teams_count',
          key: 'teams_count',
          width: 100,
        },
        {
          title: 'Событий',
          dataIndex: 'events_count',
          key: 'events_count',
          width: 100,
        },
        {
          title: 'ID пользователя',
          dataIndex: 'id',
          key: 'id',
          width: 100,
        },
      ],
      teamColumns: [
        {
          title: 'Команда',
          key: 'name',
          width: 200,
        },
        {
          title: 'Игроков',
          dataIndex: 'players_count',
          key: 'players_count',
          width: 100,
        },
        {
          title: 'Администраторов',
          dataIndex: 'admins_count',
          key: 'admins_count',
          width: 120,
        },
        {
          title: 'Событий',
          dataIndex: 'events_count',
          key: 'events_count',
          width: 100,
        },
        {
          title: 'ID команды',
          dataIndex: 'id',
          key: 'id',
          width: 100,
        },
      ],
      playerColumns: [
        {
          title: 'Игрок',
          key: 'player_info',
          width: 200,
          fixed: 'left',
        },
        {
          title: 'Команда',
          key: 'team_info',
          width: 150,
        },
        {
          title: 'Позиция',
          key: 'position',
          width: 120,
        },
        {
          title: 'Роль',
          key: 'role',
          width: 120,
        },
        {
          title: 'Капитан',
          key: 'captain_status',
          width: 100,
        },
        {
          title: 'Физические данные',
          key: 'physical_info',
          width: 150,
        },
        {
          title: 'Навыки',
          key: 'skills',
          width: 150,
        },
        {
          title: 'Статистика участия',
          key: 'participation_stats',
          width: 150,
        },
        {
          title: 'Статус',
          key: 'status',
          width: 100,
        },
        {
          title: 'Username',
          dataIndex: 'username',
          key: 'username',
          width: 120,
        },
        {
          title: 'Telegram ID',
          dataIndex: 'telegram_id',
          key: 'telegram_id',
          width: 120,
        },
        {
          title: 'Дата рождения',
          dataIndex: 'date_of_birth',
          key: 'date_of_birth',
          width: 120,
        },
        {
          title: 'Экстренный телефон',
          dataIndex: 'emergency_phone',
          key: 'emergency_phone',
          width: 150,
        },
        {
          title: 'Участие до',
          dataIndex: 'participation_until',
          key: 'participation_until',
          width: 120,
        },
        {
          title: 'По умолчанию участвует',
          dataIndex: 'default_participation',
          key: 'default_participation',
          width: 150,
        },
        {
          title: 'Комментарий по умолчанию',
          dataIndex: 'default_comment',
          key: 'default_comment',
          width: 200,
        },
        {
          title: 'Дата регистрации',
          key: 'created_at',
          width: 150,
        },
      ],
    };
  },
  computed: {
    activityChartData() {
      if (!this.stats.activity?.daily) return [];
      
      const maxCount = Math.max(...this.stats.activity.daily.map(d => d.count));
      return this.stats.activity.daily.map(day => ({
        ...day,
        height: maxCount > 0 ? (day.count / maxCount) * 100 : 0
      }));
    },
    memoryUsagePercent() {
      const memory = this.systemInfo.system?.memory;
      if (!memory) return 0;
      return Math.round((memory.used / memory.total) * 100);
    },
    // Добавляем валидацию данных
    isValidStats() {
      return this.stats && typeof this.stats === 'object';
    },
    isValidSystemInfo() {
      return this.systemInfo && typeof this.systemInfo === 'object';
    },
    hasPlayers() {
      return Array.isArray(this.players) && this.players.length > 0;
    },
    hasUsers() {
      return Array.isArray(this.users) && this.users.length > 0;
    },
    hasTeams() {
      return Array.isArray(this.teams) && this.teams.length > 0;
    },
  },
  async mounted() {
    // Проверяем авторизацию
    if (!this.checkAuth()) {
      this.$router.push('/admin/login');
      return;
    }
    
    await this.loadStats();
    await this.loadUsers();
    await this.loadTeams();
    await this.loadPlayers();
    await this.loadSystemInfo();
  },
  beforeUnmount() {
    // Очищаем таймауты при размонтировании компонента
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
  methods: {
    async loadStats() {
      try {
        this.error = null;
        this.stats = await api.getAdminPanelStats();
      } catch (error) {
        console.error('Error loading stats:', error);
        this.error = 'Ошибка загрузки статистики';
        this.$message.error('Ошибка загрузки статистики');
      }
    },
    async loadUsers(page = 1, pageSize = 20) {
      this.usersLoading = true;
      this.error = null;
      try {
        // Валидация параметров
        const validPage = Math.max(1, parseInt(page) || 1);
        const validPageSize = Math.min(100, Math.max(1, parseInt(pageSize) || 20));
        
        const response = await api.getAdminPanelUsers(validPage, validPageSize, this.userSearch);
        this.users = response.users || [];
        this.userPagination.current = response.pagination?.page || validPage;
        this.userPagination.total = response.pagination?.total || 0;
      } catch (error) {
        console.error('Error loading users:', error);
        this.error = 'Ошибка загрузки пользователей';
        this.$message.error('Ошибка загрузки пользователей');
      } finally {
        this.usersLoading = false;
      }
    },
    async loadTeams(page = 1, pageSize = 20) {
      this.teamsLoading = true;
      this.error = null;
      try {
        // Валидация параметров
        const validPage = Math.max(1, parseInt(page) || 1);
        const validPageSize = Math.min(100, Math.max(1, parseInt(pageSize) || 20));
        
        const response = await api.getAdminPanelTeams(validPage, validPageSize, this.teamSearch);
        this.teams = response.teams || [];
        this.teamPagination.current = response.pagination?.page || validPage;
        this.teamPagination.total = response.pagination?.total || 0;
      } catch (error) {
        console.error('Error loading teams:', error);
        this.error = 'Ошибка загрузки команд';
        this.$message.error('Ошибка загрузки команд');
      } finally {
        this.teamsLoading = false;
      }
    },
    async loadPlayers(page = 1, pageSize = 20) {
      this.playersLoading = true;
      this.error = null;
      try {
        // Валидация параметров
        const validPage = Math.max(1, parseInt(page) || 1);
        const validPageSize = Math.min(100, Math.max(1, parseInt(pageSize) || 20));
        
        const response = await api.getAdminPanelPlayers(
          validPage, 
          validPageSize, 
          this.playerSearch, 
          this.playerTeamFilter, 
          this.playerPositionFilter
        );
        this.players = response.players || [];
        this.playerPagination.current = response.pagination?.page || validPage;
        this.playerPagination.total = response.pagination?.total || 0;
      } catch (error) {
        console.error('Error loading players:', error);
        this.error = 'Ошибка загрузки игроков';
        this.$message.error('Ошибка загрузки игроков');
      } finally {
        this.playersLoading = false;
      }
    },
    async loadSystemInfo() {
      try {
        this.error = null;
        this.systemInfo = await api.getAdminPanelSystem();
      } catch (error) {
        console.error('Error loading system info:', error);
        this.error = 'Ошибка загрузки системной информации';
        this.$message.error('Ошибка загрузки системной информации');
      }
    },
    handleUserTableChange(pagination) {
      this.loadUsers(pagination.current, pagination.pageSize);
    },
    handleTeamTableChange(pagination) {
      this.loadTeams(pagination.current, pagination.pageSize);
    },
    handlePlayerTableChange(pagination) {
      this.loadPlayers(pagination.current, pagination.pageSize);
    },
    // Добавляем debounced поиск для улучшения производительности
    debouncedSearch(searchType) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      
      this.searchTimeout = setTimeout(() => {
        switch (searchType) {
          case 'users':
            this.loadUsers(1, this.userPagination.pageSize);
            break;
          case 'teams':
            this.loadTeams(1, this.teamPagination.pageSize);
            break;
          case 'players':
            this.loadPlayers(1, this.playerPagination.pageSize);
            break;
        }
      }, 300);
    },
    // Метод для очистки ошибок
    clearError() {
      this.error = null;
    },
    formatDate(dateString) {
      if (!dateString) return '—';
      return new Date(dateString).toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    formatChartDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit'
      });
    },
    formatUptime(seconds) {
      if (!seconds) return '—';
      const days = Math.floor(seconds / 86400);
      const hours = Math.floor((seconds % 86400) / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      
      if (days > 0) {
        return `${days}д ${hours}ч ${minutes}м`;
      } else if (hours > 0) {
        return `${hours}ч ${minutes}м`;
      } else {
        return `${minutes}м`;
      }
    },
    formatBytes(bytes) {
      if (!bytes) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    checkAuth() {
      const adminToken = localStorage.getItem('adminToken');
      const loginTime = localStorage.getItem('adminLoginTime');
      
      if (!adminToken || !loginTime) {
        return false;
      }
      
      // Проверяем, что прошло не более 24 часов с момента входа
      const now = Date.now();
      const loginTimestamp = parseInt(loginTime);
      const hoursPassed = (now - loginTimestamp) / (1000 * 60 * 60);
      
      if (hoursPassed > 24) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminLoginTime');
        return false;
      }
      
      return true;
    },

    logout() {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminLoginTime');
      this.$router.push('/admin/login');
    },
    getPositionLabel(position) {
      const labels = {
        'forward': 'Нападающий',
        'defender': 'Защитник',
        'goalkeeper': 'Вратарь',
        'coach': 'Тренер',
        'service': 'Обслуживающий персонал'
      };
      return labels[position] || position;
    },
    getPositionColor(position) {
      const colors = {
        'forward': 'green',
        'defender': 'blue',
        'goalkeeper': 'orange',
        'coach': 'purple',
        'service': 'gray'
      };
      return colors[position] || 'default';
    },
    getCaptainLabel(status) {
      const labels = {
        'captain': 'Капитан',
        'assistant': 'Ассистент'
      };
      return labels[status] || status;
    },
    getCaptainColor(status) {
      const colors = {
        'captain': 'gold',
        'assistant': 'silver'
      };
      return colors[status] || 'default';
    },
    getStickHandLabel(hand) {
      const labels = {
        'left': 'Левая',
        'right': 'Правая'
      };
      return labels[hand] || hand;
    },
    getSkillLevelLabel(level) {
      const labels = {
        'beginner': 'Начинающий',
        'intermediate': 'Средний',
        'experienced': 'Опытный'
      };
      return labels[level] || level;
    },
    getStatusLabel(status) {
      const labels = {
        'pending': 'Ожидает',
        'accepted': 'Принят',
        'rejected': 'Отклонен'
      };
      return labels[status] || status;
    },
    getStatusColor(status) {
      const colors = {
        'pending': 'orange',
        'accepted': 'green',
        'rejected': 'red'
      };
      return colors[status] || 'default';
    },
  },
};
</script>

<style scoped>
.admin-panel {
  min-height: 100vh;
  background: var(--color-bg-secondary);
  padding: var(--spacing-xl);
}

.admin-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--color-bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  padding: var(--spacing-lg) var(--spacing-xl);
  margin-bottom: var(--spacing-xl);
}

.header-content h1 {
  margin: 0;
  font-size: var(--font-size-xxl);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
}

.header-content p {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-lg);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.logout-button {
  color: var(--color-error);
  font-weight: var(--font-weight-semibold);
}

.admin-tabs {
  background: var(--color-bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  padding: var(--spacing-lg);
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-xl);
}

.stat-card {
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  padding: var(--spacing-lg);
}

.stat-content {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.stat-icon {
  font-size: var(--font-size-xxxl);
  color: var(--color-primary);
}

.stat-info {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: var(--font-size-xxl);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
}

.stat-label {
  color: var(--color-text-secondary);
  font-size: var(--font-size-md);
}

.stat-detail {
  color: var(--color-text-tertiary);
  font-size: var(--font-size-sm);
}

/* График активности */
.activity-chart-card {
  margin-bottom: 24px;
}

.activity-chart {
  display: flex;
  align-items: end;
  gap: 8px;
  height: 200px;
  padding: 20px 0;
}

.chart-bar {
  flex: 1;
  background: #1890ff;
  border-radius: 4px 4px 0 0;
  min-height: 20px;
  position: relative;
  transition: all 0.3s ease;
}

.chart-bar:hover {
  background: #40a9ff;
}

.bar-value {
  position: absolute;
  top: -25px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 12px;
  color: #8c8c8c;
}

.chart-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 12px;
  color: #8c8c8c;
}

/* Города */
.cities-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.city-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.city-name {
  font-weight: 500;
}

.city-count {
  color: #8c8c8c;
  font-size: 14px;
}

/* Таблицы */
.search-section {
  margin-bottom: 16px;
}

/* Фильтры игроков */
.filters-section {
  margin-bottom: 16px;
}

.filters-row {
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
}

/* Информация об игроке */
.player-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.player-details {
  display: flex;
  flex-direction: column;
}

.player-name {
  font-weight: 500;
  color: var(--color-text-primary);
}

.player-number {
  font-size: 12px;
  color: var(--color-text-secondary);
}

/* Физические данные и навыки */
.physical-info, .skills-info, .participation-stats {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
}

.physical-info div, .skills-info div, .participation-stats div {
  color: var(--color-text-secondary);
}

.no-status {
  color: #d9d9d9;
  font-style: italic;
}

.user-info, .team-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.team-name {
  font-weight: 500;
}

.team-city {
  color: #8c8c8c;
  font-size: 12px;
}

.username-link {
  color: #1890ff;
  text-decoration: none;
}

.username-link:hover {
  text-decoration: underline;
}

.no-username {
  color: #d9d9d9;
}

/* Системная информация */
.system-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 16px;
}

.system-card {
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.system-info, .database-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.info-label {
  color: #8c8c8c;
  font-size: 14px;
}

.info-value {
  font-weight: 500;
  color: #262626;
}

/* Память */
.memory-info {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.memory-bar {
  height: 20px;
  background: #f0f0f0;
  border-radius: 10px;
  overflow: hidden;
}

.memory-used {
  height: 100%;
  background: linear-gradient(90deg, #52c41a, #1890ff);
  transition: width 0.3s ease;
}

.memory-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.memory-item {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
}

.memory-item span:first-child {
  color: #8c8c8c;
}

.memory-item span:last-child {
  font-weight: 500;
}

/* Адаптивность */
@media (max-width: 768px) {
  .admin-panel {
    padding: 16px;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .system-grid {
    grid-template-columns: 1fr;
  }
  
  .activity-chart {
    height: 150px;
  }
}

/* Стили для отображения ошибок */
.error-banner {
  margin-bottom: 16px;
}

.error-banner .ant-alert {
  border-radius: var(--border-radius-md);
}
</style> 